import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import './PdfViewer.css'
import { PdfSearchPanel, PdfSearchToggle } from './search'
import { MarkupLayer, MarkupToggle, MarkupToolbar, useMarkupTools } from './markup'
import { useMarkups } from '../../hooks/useMarkups'
import { compileHighlightRules, highlightString, markRanges, rangesByItem } from './highlightText'

// PDF.js runs its parser in a web worker. CRA (webpack 5) turns this
// `new URL(..., import.meta.url)` into an emitted asset, so the worker version
// always matches the bundled pdfjs-dist.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString()

// When a <Document> unmounts mid-stream (e.g. a searchroom is deleted while its
// PDF is still loading over HTTP Range), PDF.js aborts the in-flight request and
// its stream reader's cancel() rejects with a benign AbortError ("signal is
// aborted without reason") that nothing awaits — surfacing as an unhandled
// rejection that trips the dev error overlay. Swallow just that case, once.
if (typeof window !== 'undefined' && !window.__pdfAbortGuardInstalled) {
  window.__pdfAbortGuardInstalled = true
  window.addEventListener('unhandledrejection', (event) => {
    if (event?.reason?.name === 'AbortError') event.preventDefault()
  })
}

const MIN_SCALE = 0.1
const MAX_SCALE = 3
const SCALE_STEP = 0.2

// Browsers cap how large a <canvas> can be. Above these, the canvas silently
// renders blank (white) — which is what happens on large/tall pages (foldouts,
// big figures) once width × height × devicePixelRatio gets too big. We pick a
// per-page devicePixelRatio that keeps the backing canvas within these limits.
const MAX_CANVAS_DIM = 8192
const MAX_CANVAS_AREA = 16_000_000

function safeDevicePixelRatio(cssWidth, cssHeight) {
  const base = Math.min((typeof window !== 'undefined' && window.devicePixelRatio) || 1, 2)
  if (!cssWidth || !cssHeight) return base
  const byDim = Math.min(MAX_CANVAS_DIM / cssWidth, MAX_CANVAS_DIM / cssHeight)
  const byArea = Math.sqrt(MAX_CANVAS_AREA / (cssWidth * cssHeight))
  return Math.max(0.75, Math.min(base, byDim, byArea))
}

// Per-item fallback used until a page's text items have been indexed. Returns
// null when there is nothing to highlight.
function buildTextHighlighter(rules) {
  const regexes = compileHighlightRules(rules)
  if (!regexes.length) return null
  return (str) => highlightString(str, regexes)
}

// How long a programmatic jump keeps trying to re-anchor before it gives up (ms).
// Pages mount and settle to their real heights asynchronously after we scroll,
// so we re-issue the scroll on each layout change within this window.
const ANCHOR_TTL_MS = 1500

const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
}

const IconRotateCcw = () => (
  <svg {...svgProps}>
    <polyline points="1 4 1 10 7 10" />
    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
  </svg>
)

const IconRotateCw = () => (
  <svg {...svgProps}>
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
)

const IconGrid = () => (
  <svg {...svgProps}>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
)

const GRID_SIZE_MIN = 120
const GRID_SIZE_MAX = 480

// A reusable PDF viewer built on react-pdf (PDF.js). It renders a scrollable run
// of pages and is deliberately decoupled from any one feature so it can back the
// searchroom viewer, chat-citation jumps, notebooks, etc.
//
// Scroll accuracy: pages are windowed (only those near the viewport mount a real
// canvas; the rest are sized placeholders), so a naive one-shot scroll lands off
// by a page or several once real heights replace the estimates, and drifts again
// after a width change. To fix that we (1) remember the intended target page and
// re-anchor to it whenever page geometry changes until it settles, (2) cache each
// page's real rendered height so placeholders stop mis-estimating, and (3) on a
// width change (pane maximize / zoom) re-pin the current page across the reflow.
//
// Props
//   fileUrl        URL to stream the PDF from (server sends it with Range support)
//   page           page to scroll to once loaded (defaults to the range start)
//   pageRange      [start, end] (1-based, inclusive) to render; omit for all pages
//   withCredentials send cookies with the request (our PDF route is authed)
//   standard       the document's standard name (enables the PDF search popover
//                  and scopes search to this document)
//   renderPageOverlay(pageNumber) optional render-prop for future overlays
//                  (highlights, citation markers) drawn on top of a page
//   actions        optional nodes rendered on the toolbar's right (e.g. a
//                  pane-level search button), so the viewer needs no extra bar
//   onDocumentLoad({ numPages }) called after the document loads
//   pageToken      bump to jump to `page` again even when the number is unchanged
//                  (history back can return to a page the viewer already targeted)
function PdfViewer({
  fileUrl,
  page,
  pageToken,
  pageRange,
  withCredentials = true,
  renderPageOverlay,
  highlightRules,
  standard,
  actions,
  onDocumentLoad,
  onPageChange,
  className = '',
}) {
  const [numPages, setNumPages] = useState(0)
  const [status, setStatus] = useState('loading') // loading | ready | error
  const [scale, setScale] = useState(1)
  const [baseWidth, setBaseWidth] = useState(0)
  const [currentPage, setCurrentPage] = useState(page || 1)
  // Only pages near the viewport actually mount a <Page> (canvas + text layer).
  // Without this, a large range (e.g. a whole standard, ~750 pages) would render
  // hundreds of canvases at once and freeze the browser.
  const [renderPages, setRenderPages] = useState(() => new Set())
  // Temporary per-page USER rotation override in absolute degrees (0/90/180/270),
  // keyed by page number. Absent = no override, so react-pdf honors the page's
  // own intrinsic /Rotate. View-only; cleared when the document reloads.
  const [rotations, setRotations] = useState({})
  // Each page's intrinsic /Rotate, captured on load, so the rotate buttons turn
  // relative to how the page actually displays (not from a forced 0).
  const intrinsicRotationsRef = useRef(new Map())
  // Per-page aspect ratio (height/width) from the loaded page, so we can size the
  // canvas and pick a safe devicePixelRatio. Pages that failed to render (blank
  // white — usually an oversized canvas or an image PDF.js can't decode) are
  // tracked so we can show a retry instead of nothing.
  const [pageAspects, setPageAspects] = useState({})
  const [renderErrors, setRenderErrors] = useState({})
  // Editable page-jump input. Draft mirrors currentPage unless the user is
  // typing; is-error briefly flashes on malformed/out-of-range entries.
  const [pageDraft, setPageDraft] = useState(String(page || 1))
  const [pageError, setPageError] = useState(false)
  const pageInputFocused = useRef(false)
  const pageErrorTimer = useRef(0)
  // In-document search popover and active highlight rules (from the last run).
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchHighlightRules, setSearchHighlightRules] = useState([])
  // Pages the in-document search should highlight. Null highlights every page.
  const [searchHighlightRange, setSearchHighlightRange] = useState(null)
  // Thumbnail grid. `gridSize` is the width of each page in that view.
  const [gridView, setGridView] = useState(false)
  const [gridSize, setGridSize] = useState(200)
  // PDF.js text items per page, and the highlight ranges computed from them.
  // A phrase is often split across items, so per-item matching misses it.
  const textItemsRef = useRef(new Map())
  const [textTick, setTextTick] = useState(0)
  const [pageHighlights, setPageHighlights] = useState({})
  const markupTools = useMarkupTools()
  const { byPage: markupsByPage, add: addMarkup, update: updateMarkup, remove: removeMarkup } =
    useMarkups(standard)
  const [selectedMarkupId, setSelectedMarkupId] = useState(null)

  useEffect(() => {
    setSelectedMarkupId(null)
  }, [fileUrl, standard, markupTools.enabled])

  useEffect(() => {
    if (!markupTools.enabled) return undefined
    const onKey = (e) => {
      const tag = e.target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return
      if (e.key === 'Escape') {
        setSelectedMarkupId(null)
        return
      }
      if (e.key !== 'Delete' && e.key !== 'Backspace') return
      if (!selectedMarkupId) return
      e.preventDefault()
      removeMarkup(selectedMarkupId)
      setSelectedMarkupId(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [markupTools.enabled, selectedMarkupId, removeMarkup])

  const scrollRef = useRef(null)
  const pageRefs = useRef(new Map())
  const didInitialScroll = useRef(false)

  // Real, measured wrapper heights per page (px at the current width). Used to
  // size placeholders accurately; cleared when the width changes (stale scale).
  const pageHeightsRef = useRef(new Map())
  // The page a programmatic jump is trying to land on (null when idle). While
  // set, layout changes re-issue the scroll so we stay pinned as pages settle.
  const pendingTargetRef = useRef(null)
  const pendingTimerRef = useRef(0)
  // True only during our own scrollIntoView calls, so a genuine user scroll can
  // be told apart from the scroll events our re-anchoring produces.
  const programmaticRef = useRef(false)
  const progTimerRef = useRef(0)
  // True while the pane width is changing (sidebar expand, pane drag). Layout
  // shifts fire scroll events; those must not be read as the reader moving.
  const reflowRef = useRef(false)
  const reflowTimerRef = useRef(0)
  // Live mirror of currentPage for use inside observers without re-subscribing.
  const currentPageRef = useRef(currentPage)
  currentPageRef.current = currentPage
  const gridViewRef = useRef(false)
  gridViewRef.current = gridView
  const prevWidthRef = useRef(0)

  // Report the most-visible page upward without forcing the effect to re-run on
  // every render when the parent passes an inline callback.
  const onPageChangeRef = useRef(onPageChange)
  onPageChangeRef.current = onPageChange
  useEffect(() => {
    onPageChangeRef.current?.(currentPage)
  }, [currentPage])

  // Scroll a page wrapper to the top of the viewport, flagging it as our own
  // scroll so handleScroll doesn't mistake it for the user taking over.
  const scrollNodeToTop = useCallback((node) => {
    if (!node) return
    programmaticRef.current = true
    node.scrollIntoView({ block: 'start' })
    window.clearTimeout(progTimerRef.current)
    progTimerRef.current = window.setTimeout(() => {
      programmaticRef.current = false
    }, 160)
  }, [])

  // Arm/replace the pending jump target (auto-expires so it can't linger and
  // yank the view long after the jump).
  const setPendingTarget = useCallback((target) => {
    pendingTargetRef.current = target
    window.clearTimeout(pendingTimerRef.current)
    if (target != null) {
      pendingTimerRef.current = window.setTimeout(() => {
        pendingTargetRef.current = null
      }, ANCHOR_TTL_MS)
    }
  }, [])

  // Jump to a page: force it to mount (so we align to its real content, not a
  // placeholder), scroll to it, and keep it pending so we re-anchor as heights
  // settle around it.
  const anchorToPage = useCallback(
    (target) => {
      if (!target) return
      setPendingTarget(target)
      setRenderPages((prev) => (prev.has(target) ? prev : new Set(prev).add(target)))
      scrollNodeToTop(pageRefs.current.get(target))
    },
    [setPendingTarget, scrollNodeToTop]
  )

  // Re-issue the scroll to the pending target (called whenever geometry shifts).
  const reanchor = useCallback(() => {
    const target = pendingTargetRef.current
    if (target == null) return
    scrollNodeToTop(pageRefs.current.get(target))
  }, [scrollNodeToTop])
  const reanchorRef = useRef(reanchor)
  reanchorRef.current = reanchor

  // One ResizeObserver over every page wrapper: records real heights and, while a
  // jump is pending, re-anchors to the target when a mount/height change moves it.
  const heightObs = useMemo(() => {
    if (typeof ResizeObserver === 'undefined') return null
    return new ResizeObserver((entries) => {
      for (const entry of entries) {
        const n = Number(entry.target.dataset.page)
        // Grid thumbs are a different size. Recording them would replace the
        // full-page heights the single-page view uses for placeholders.
        if (n && entry.contentRect.height > 0 && !gridViewRef.current) {
          pageHeightsRef.current.set(n, entry.contentRect.height)
        }
      }
      reanchorRef.current()
    })
  }, [])

  useEffect(() => () => heightObs && heightObs.disconnect(), [heightObs])

  // Keep the Document `file`/`options` identities stable so react-pdf doesn't
  // reload the document on every render.
  const file = useMemo(
    () => (fileUrl ? { url: fileUrl, withCredentials } : null),
    [fileUrl, withCredentials]
  )
  // PDF.js loads its image-decoder WebAssembly (OpenJPEG for JPEG 2000/JPX, qcms
  // for color) from `wasmUrl` at runtime. These files are copied into public/ by
  // scripts/copy-pdf-assets.js; without this, JPX-heavy pages fail to decode
  // ("OpenJPEG failed to initialize") and render blank. Must end with a slash.
  const options = useMemo(
    () => ({ wasmUrl: `${process.env.PUBLIC_URL || ''}/pdfjs/wasm/` }),
    []
  )

  // Wrap search matches in the text layer (undefined -> react-pdf default render).
  // Merge externally supplied highlight rules (e.g. Global Text Search hits)
  // with the viewer's own in-document search rules.
  const activeHighlightRules = useMemo(
    () => [...(highlightRules || []), ...searchHighlightRules],
    [highlightRules, searchHighlightRules]
  )
  const rulesForPage = useCallback(
    (pageNumber) => {
      const inScope =
        !searchHighlightRange ||
        (pageNumber >= searchHighlightRange[0] && pageNumber <= searchHighlightRange[1])
      if (!searchHighlightRules.length || inScope) return activeHighlightRules
      return highlightRules || []
    },
    [searchHighlightRange, searchHighlightRules.length, activeHighlightRules, highlightRules]
  )

  // Recompute cross-item highlights whenever rules change or a page's text arrives.
  useEffect(() => {
    const itemsByPage = textItemsRef.current
    if (!itemsByPage.size) {
      setPageHighlights({})
      return
    }
    const next = {}
    for (const [pageNumber, items] of itemsByPage) {
      const regexes = compileHighlightRules(rulesForPage(pageNumber))
      next[pageNumber] = regexes.length ? rangesByItem(items, regexes) : null
    }
    setPageHighlights(next)
  }, [textTick, rulesForPage])

  // In-document search can be limited to the current page or a range. Highlights
  // follow that limit; rules passed in from outside (global text search) do not.
  const textRendererFor = (pageNumber) => {
    const rules = rulesForPage(pageNumber)
    if (!compileHighlightRules(rules).length) return undefined
    if (!Object.prototype.hasOwnProperty.call(pageHighlights, pageNumber)) {
      return buildTextHighlighter(rules)
    }
    const byItem = pageHighlights[pageNumber]
    if (!byItem || !Object.keys(byItem).length) return undefined
    return ({ str, itemIndex }) => markRanges(str, byItem[itemIndex])
  }

  // Which pages to render: the requested range, clamped to the document.
  const pages = useMemo(() => {
    if (!numPages) return []
    let start = 1
    let end = numPages
    if (Array.isArray(pageRange) && pageRange.length === 2) {
      start = Math.max(1, Math.min(pageRange[0] || 1, numPages))
      end = Math.max(start, Math.min(pageRange[1] || numPages, numPages))
    }
    const list = []
    for (let n = start; n <= end; n += 1) list.push(n)
    return list
  }, [numPages, pageRange])

  // Reset when the source document changes.
  useEffect(() => {
    setStatus('loading')
    setNumPages(0)
    setRenderPages(new Set())
    setRotations({})
    setSearchOpen(false)
    setSearchHighlightRules([])
    didInitialScroll.current = false
    pageRefs.current.clear()
    pageHeightsRef.current.clear()
    intrinsicRotationsRef.current.clear()
    setPageAspects({})
    setRenderErrors({})
    pendingTargetRef.current = null
    textItemsRef.current = new Map()
    setPageHighlights({})
    setTextTick(0)
  }, [fileUrl])

  // Rotation is temporary/view-only. On the Code page the viewer stays mounted
  // across items of the same standard (same fileUrl), so also clear rotations
  // when a different item/target page is opened — otherwise a rotation applied
  // under one item would carry over to another item that covers the same page.
  useEffect(() => {
    setRotations({})
  }, [page])

  // Track the container's width so pages render responsively (fit-to-width). On a
  // real width change (pane maximize / layout shift) cached heights become stale,
  // and we re-pin the current page so the reflow doesn't drift it.
  useLayoutEffect(() => {
    const node = scrollRef.current
    if (!node) return undefined
    const measure = () => {
      const width = node.clientWidth - 32
      if (
        prevWidthRef.current !== 0 &&
        width !== prevWidthRef.current &&
        !gridViewRef.current
      ) {
        reflowRef.current = true
        window.clearTimeout(reflowTimerRef.current)
        pageHeightsRef.current.clear()
        if (currentPageRef.current) setPendingTarget(currentPageRef.current)
        // Drop the guard shortly after the width stops changing, so a real
        // scroll right after the drag still counts.
        reflowTimerRef.current = window.setTimeout(() => {
          reflowRef.current = false
        }, 300)
      }
      prevWidthRef.current = width
      setBaseWidth(width)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => {
      observer.disconnect()
      window.clearTimeout(reflowTimerRef.current)
    }
  }, [setPendingTarget])

  const handleLoadSuccess = useCallback(
    (pdf) => {
      setNumPages(pdf.numPages)
      setStatus('ready')
      onDocumentLoad?.({ numPages: pdf.numPages })
    },
    [onDocumentLoad]
  )

  const handleLoadError = useCallback(() => setStatus('error'), [])

  const setPageRef = useCallback(
    (pageNumber, node) => {
      const prev = pageRefs.current.get(pageNumber)
      if (prev && heightObs) heightObs.unobserve(prev)
      if (node) {
        pageRefs.current.set(pageNumber, node)
        if (heightObs) heightObs.observe(node)
      } else {
        pageRefs.current.delete(pageNumber)
      }
    },
    [heightObs]
  )

  // Jump to the requested page: once after load, and again whenever `page`
  // or `pageToken` changes (a new item, or history back to a page already set).
  useEffect(() => {
    if (status !== 'ready' || !pages.length) return undefined
    const target = page && pages.includes(page) ? page : pages[0]
    // Defer so the page elements exist in the DOM.
    const id = window.requestAnimationFrame(() => {
      anchorToPage(target)
      setCurrentPage(target)
      didInitialScroll.current = true
    })
    return () => window.cancelAnimationFrame(id)
  }, [status, pages, page, pageToken, anchorToPage])

  // The page under a reading line near the top of the viewport. A tall page in a
  // short pane never covers 25% of itself, so an intersection-ratio observer
  // never fires and the location breadcrumb stays stuck on the page that opened.
  const syncVisiblePage = useCallback(() => {
    const root = scrollRef.current
    if (!root || !pages.length) return
    // Grid scrolling is a survey. The page number stays put until a thumbnail
    // is clicked. Scroll still moves the page in the single-page view.
    if (gridViewRef.current) return
    const probe = root.getBoundingClientRect().top + Math.min(24, root.clientHeight * 0.2)
    let lo = 0
    let hi = pages.length - 1
    let found = null
    while (lo <= hi) {
      const mid = (lo + hi) >> 1
      const node = pageRefs.current.get(pages[mid])
      if (!node) return
      if (node.getBoundingClientRect().top <= probe) {
        found = pages[mid]
        lo = mid + 1
      } else {
        hi = mid - 1
      }
    }
    const next = found || pages[0]
    if (next !== currentPageRef.current) setCurrentPage(next)
  }, [pages])
  const syncVisiblePageRef = useRef(syncVisiblePage)
  syncVisiblePageRef.current = syncVisiblePage
  const visibleRafRef = useRef(0)

  // Windowing: mount <Page> only for pages within a generous margin of the
  // viewport, and unmount those that scroll far away. Page wrappers always exist
  // (as sized placeholders) so scroll geometry and the observers stay stable.
  useEffect(() => {
    const root = scrollRef.current
    if (status !== 'ready' || !root || !pages.length) return undefined
    const observer = new IntersectionObserver(
      (entries) => {
        setRenderPages((prev) => {
          const next = new Set(prev)
          let changed = false
          for (const e of entries) {
            const n = Number(e.target.dataset.page)
            if (!n) continue
            if (e.isIntersecting) {
              if (!next.has(n)) {
                next.add(n)
                changed = true
              }
            } else if (next.has(n)) {
              next.delete(n)
              changed = true
            }
          }
          return changed ? next : prev
        })
      },
      { root, rootMargin: '1200px 0px', threshold: 0 }
    )
    pageRefs.current.forEach((node) => observer.observe(node))
    return () => observer.disconnect()
  }, [status, pages, gridView])

  // A genuine user scroll cancels any in-flight jump so re-anchoring never yanks
  // the user back to a target they've chosen to scroll away from.
  const handleScroll = useCallback(() => {
    if (programmaticRef.current || reflowRef.current) return
    pendingTargetRef.current = null
    if (visibleRafRef.current) return
    visibleRafRef.current = window.requestAnimationFrame(() => {
      visibleRafRef.current = 0
      syncVisiblePageRef.current()
    })
  }, [])

  useEffect(() => () => window.cancelAnimationFrame(visibleRafRef.current), [])

  const zoom = useCallback((delta) => {
    setScale((s) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, +(s + delta).toFixed(2))))
  }, [])

  // Leave the grid and open one page at full size. Deferred a frame so the
  // single-page layout exists before the scroll is measured.
  const leaveGrid = useCallback(
    (pageNumber) => {
      const target = pageNumber || currentPageRef.current
      setGridView(false)
      if (!target) return
      setCurrentPage(target)
      window.requestAnimationFrame(() => anchorToPage(target))
    },
    [anchorToPage]
  )

  const toggleGrid = useCallback(() => {
    if (gridViewRef.current) leaveGrid(currentPageRef.current)
    else setGridView(true)
  }, [leaveGrid])

  // Bring the page you were reading into the grid when the view opens.
  useEffect(() => {
    if (!gridView || status !== 'ready') return undefined
    const id = window.requestAnimationFrame(() => {
      pageRefs.current.get(currentPageRef.current)?.scrollIntoView({
        block: 'center',
        inline: 'nearest',
      })
    })
    return () => window.cancelAnimationFrame(id)
  }, [gridView, status])

  // Force one page to remount (clears its error and re-attempts the render).
  const retryPage = useCallback((n) => {
    setRenderErrors((prev) => {
      if (!prev[n]) return prev
      const next = { ...prev }
      delete next[n]
      return next
    })
    setRenderPages((prev) => {
      const next = new Set(prev)
      next.delete(n)
      return next
    })
    window.requestAnimationFrame(() =>
      setRenderPages((prev) => (prev.has(n) ? prev : new Set(prev).add(n)))
    )
  }, [])

  // Closing the search bar also clears its highlights (they shouldn't linger on
  // the page after the bar is gone).
  const closeSearch = useCallback(() => {
    setSearchOpen(false)
    setSearchHighlightRules([])
    setSearchHighlightRange(null)
  }, [])

  // Rotate the current (most-visible) page by ±90°, keeping it pinned as its
  // height changes. Rotation is per-page and view-only.
  const rotateCurrent = useCallback(
    (delta) => {
      const target = currentPageRef.current
      if (!target) return
      setRotations((prev) => {
        // Turn relative to the current display: a user override if set, else the
        // page's intrinsic rotation, else 0.
        const base =
          prev[target] !== undefined
            ? prev[target]
            : intrinsicRotationsRef.current.get(target) || 0
        const next = (((base + delta) % 360) + 360) % 360
        return { ...prev, [target]: next }
      })
      anchorToPage(target)
    },
    [anchorToPage]
  )

  const positionInRange = pages.indexOf(currentPage)
  const goRelative = useCallback(
    (delta) => {
      const idx = pages.indexOf(currentPage)
      const next = pages[idx + delta]
      if (next) anchorToPage(next)
    },
    [pages, currentPage, anchorToPage]
  )

  // Keep the input in sync with the page the viewport settles on, but never
  // clobber what the user is actively typing.
  useEffect(() => {
    if (!pageInputFocused.current) setPageDraft(String(currentPage))
  }, [currentPage])

  useEffect(() => () => window.clearTimeout(pageErrorTimer.current), [])

  const flashPageError = useCallback(() => {
    setPageError(true)
    window.clearTimeout(pageErrorTimer.current)
    pageErrorTimer.current = window.setTimeout(() => setPageError(false), 600)
  }, [])

  // Commit the typed page: malformed input reverts to the current page; a valid
  // but out-of-range number clamps to the nearest rendered page. Both cases flash.
  const commitPage = useCallback(() => {
    if (!pages.length) return
    const first = pages[0]
    const last = pages[pages.length - 1]
    const n = parseInt(pageDraft, 10)
    if (Number.isNaN(n)) {
      flashPageError()
      setPageDraft(String(currentPage))
      return
    }
    const target = Math.min(last, Math.max(first, n))
    if (target !== n) flashPageError()
    if (target !== currentPage) anchorToPage(target)
    setPageDraft(String(target))
  }, [pages, pageDraft, currentPage, anchorToPage, flashPageError])

  const pageWidth = baseWidth > 0 ? Math.round(baseWidth * scale) : undefined
  // Estimated page height for placeholders (US Letter ratio ~1.294) so the
  // scroll extent is roughly right before a page has actually rendered.
  const estimatedHeight = pageWidth ? Math.round(pageWidth * 1.294) : 800

  if (!fileUrl) {
    return <div className="pdfviewer-empty">No PDF selected.</div>
  }

  return (
    <div className={`pdfviewer ${className}`.trim()}>
      <div className="pdfviewer-main">
      <div
        className={'pdfviewer-scroll' + (gridView ? ' is-grid' : '')}
        style={gridView ? { '--pdf-grid-size': `${gridSize}px` } : undefined}
        ref={scrollRef}
        onScroll={handleScroll}
      >
        <Document
          file={file}
          options={options}
          onLoadSuccess={handleLoadSuccess}
          onLoadError={handleLoadError}
          loading={<div className="pdfviewer-message">Loading PDF…</div>}
          error={<div className="pdfviewer-message">Could not load this PDF.</div>}
          noData={<div className="pdfviewer-message">No PDF to display.</div>}
        >
          {pages.map((pageNumber) => {
            const shouldRender = renderPages.has(pageNumber)
            const aspect = pageAspects[pageNumber] || 1.294
            const renderWidth = gridView ? gridSize : pageWidth
            const placeholderHeight = gridView
              ? Math.round(gridSize * aspect)
              : pageHeightsRef.current.get(pageNumber) || estimatedHeight
            // Keep the backing canvas within browser limits: size it from the
            // page's real aspect and cap the pixel ratio accordingly. Thumbs
            // stay at 1x so a full grid of pages does not allocate huge canvases.
            const pageDpr =
              !gridView && renderWidth
                ? safeDevicePixelRatio(renderWidth, renderWidth * aspect)
                : undefined
            const failed = Boolean(renderErrors[pageNumber])
            const openThisPage = () => leaveGrid(pageNumber)
            return (
              <div
                key={pageNumber}
                className={
                  'pdfviewer-page' +
                  (gridView ? ' is-grid' : '') +
                  (gridView && pageNumber === currentPage ? ' is-current' : '')
                }
                data-page={pageNumber}
                ref={(node) => setPageRef(pageNumber, node)}
                style={
                  gridView
                    ? { width: gridSize }
                    : shouldRender
                      ? undefined
                      : { minHeight: placeholderHeight }
                }
                onClick={gridView ? openThisPage : undefined}
                title={gridView ? `Open page ${pageNumber}` : undefined}
              >
                <div className="pdfviewer-page-inner">
                  {shouldRender && failed ? (
                    <div className="pdfviewer-page-error" style={{ minHeight: placeholderHeight }}>
                      <span>Couldn’t render page {pageNumber}.</span>
                      <button type="button" onClick={() => retryPage(pageNumber)}>
                        Retry
                      </button>
                    </div>
                  ) : shouldRender ? (
                    <Page
                      pageNumber={pageNumber}
                      width={renderWidth}
                      devicePixelRatio={gridView ? 1 : pageDpr}
                      rotate={rotations[pageNumber]}
                      onLoadSuccess={(p) => {
                        intrinsicRotationsRef.current.set(pageNumber, p.rotate || 0)
                        try {
                          const vp = p.getViewport({ scale: 1 })
                          const asp = vp.height / vp.width
                          if (asp && Number.isFinite(asp)) {
                            setPageAspects((a) =>
                              a[pageNumber] === asp ? a : { ...a, [pageNumber]: asp }
                            )
                          }
                        } catch {
                          /* ignore viewport read errors */
                        }
                      }}
                      onRenderError={(err) => {
                        // eslint-disable-next-line no-console
                        console.warn(`[PdfViewer] page ${pageNumber} failed to render:`, err)
                        setRenderErrors((e) => (e[pageNumber] ? e : { ...e, [pageNumber]: true }))
                      }}
                      onRenderSuccess={() =>
                        setRenderErrors((e) => {
                          if (!e[pageNumber]) return e
                          const next = { ...e }
                          delete next[pageNumber]
                          return next
                        })
                      }
                      renderTextLayer={!gridView}
                      renderAnnotationLayer={!gridView}
                      onGetTextSuccess={
                        gridView
                          ? undefined
                          : (textContent) => {
                              const items = textContent?.items || []
                              if (textItemsRef.current.get(pageNumber) === items) return
                              textItemsRef.current.set(pageNumber, items)
                              setTextTick((n) => n + 1)
                            }
                      }
                      customTextRenderer={gridView ? undefined : textRendererFor(pageNumber)}
                      loading={
                        <div
                          className="pdfviewer-page-skeleton"
                          style={{ height: placeholderHeight }}
                        />
                      }
                    />
                  ) : (
                    <div
                      className="pdfviewer-page-skeleton"
                      style={{ height: placeholderHeight }}
                    />
                  )}
                  {shouldRender && !gridView && (markupTools.enabled || renderPageOverlay) ? (
                    <div className="pdfviewer-overlay">
                      {markupTools.enabled ? (
                        <MarkupLayer
                          pageNumber={pageNumber}
                          markups={markupsByPage[pageNumber] || []}
                          rotation={
                            rotations[pageNumber] !== undefined
                              ? rotations[pageNumber]
                              : intrinsicRotationsRef.current.get(pageNumber) || 0
                          }
                          tool={markupTools.tool}
                          color={markupTools.color}
                          selectedId={selectedMarkupId}
                          onSelect={setSelectedMarkupId}
                          onCreate={addMarkup}
                          onUpdate={updateMarkup}
                        />
                      ) : null}
                      {renderPageOverlay?.(pageNumber)}
                    </div>
                  ) : null}
                </div>
                {gridView ? <span className="pdfviewer-grid-label">{pageNumber}</span> : null}
              </div>
            )
          })}
        </Document>
      </div>

      <div className="pdfviewer-toolbar">
        <div className="pdfviewer-pagenav">
          <button
            type="button"
            onClick={() => goRelative(-1)}
            disabled={status !== 'ready' || positionInRange <= 0}
            title="Previous page"
            aria-label="Previous page"
          >
            ‹
          </button>
          {status === 'ready' && pages.length ? (
            <span className="pdfviewer-pagejump">
              <input
                className={`pdfviewer-pageinput${pageError ? ' is-error' : ''}`}
                type="text"
                inputMode="numeric"
                value={pageDraft}
                aria-label="Page number"
                onChange={(e) => setPageDraft(e.target.value)}
                onFocus={(e) => {
                  pageInputFocused.current = true
                  e.target.select()
                }}
                onBlur={() => {
                  pageInputFocused.current = false
                  commitPage()
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.currentTarget.blur()
                  } else if (e.key === 'Escape') {
                    setPageDraft(String(currentPage))
                    pageInputFocused.current = false
                    e.currentTarget.blur()
                  }
                }}
              />
              <span className="pdfviewer-pagetotal">/ {pages[pages.length - 1]}</span>
            </span>
          ) : (
            <span className="pdfviewer-pagelabel">—</span>
          )}
          <button
            type="button"
            onClick={() => goRelative(1)}
            disabled={status !== 'ready' || positionInRange >= pages.length - 1}
            title="Next page"
            aria-label="Next page"
          >
            ›
          </button>
        </div>
        <div className="pdfviewer-toolbar-right">
          <button
            type="button"
            className={gridView ? 'is-active' : ''}
            onClick={toggleGrid}
            disabled={status !== 'ready' || !pages.length}
            title={gridView ? 'Page view' : 'Grid view'}
            aria-label={gridView ? 'Page view' : 'Grid view'}
            aria-pressed={gridView}
          >
            <IconGrid />
          </button>
          {gridView ? (
            <label className="pdfviewer-gridzoom">
              <input
                type="range"
                min={GRID_SIZE_MIN}
                max={GRID_SIZE_MAX}
                step={20}
                value={gridSize}
                onChange={(event) => setGridSize(Number(event.target.value))}
                aria-label="Grid page size"
                title="Page size"
              />
            </label>
          ) : null}
          {standard ? (
            <>
              <MarkupToggle
                on={markupTools.enabled}
                onClick={markupTools.toggle}
                disabled={status !== 'ready' || !pages.length}
              />
              {markupTools.enabled ? (
                <MarkupToolbar
                  tool={markupTools.tool}
                  onTool={(t) => {
                    markupTools.setTool(t)
                    if (t !== 'select') setSelectedMarkupId(null)
                  }}
                  color={markupTools.color}
                  onColor={(c) => {
                    markupTools.setColor(c)
                    if (selectedMarkupId) updateMarkup(selectedMarkupId, { style: { color: c } })
                  }}
                  canDelete={Boolean(selectedMarkupId)}
                  onDelete={() => {
                    if (!selectedMarkupId) return
                    removeMarkup(selectedMarkupId)
                    setSelectedMarkupId(null)
                  }}
                  disabled={status !== 'ready' || !pages.length}
                />
              ) : null}
            </>
          ) : null}
          {standard ? (
            <PdfSearchToggle
              open={searchOpen}
              onClick={() => (searchOpen ? closeSearch() : setSearchOpen(true))}
              disabled={status !== 'ready' || !pages.length}
            />
          ) : null}
          <div className="pdfviewer-rotate">
            <button
              type="button"
              onClick={() => rotateCurrent(-90)}
              disabled={status !== 'ready' || !pages.length}
              title="Rotate page left"
              aria-label="Rotate page left"
            >
              <IconRotateCcw />
            </button>
            <button
              type="button"
              onClick={() => rotateCurrent(90)}
              disabled={status !== 'ready' || !pages.length}
              title="Rotate page right"
              aria-label="Rotate page right"
            >
              <IconRotateCw />
            </button>
          </div>
          {gridView ? null : (
          <div className="pdfviewer-zoom">
            <button
              type="button"
              onClick={() => zoom(-SCALE_STEP)}
              disabled={scale <= MIN_SCALE}
              title="Zoom out"
              aria-label="Zoom out"
            >
              −
            </button>
            <button
              type="button"
              onClick={() => setScale(1)}
              title="Reset zoom"
              aria-label="Reset zoom"
            >
              {Math.round(scale * 100)}%
            </button>
            <button
              type="button"
              onClick={() => zoom(SCALE_STEP)}
              disabled={scale >= MAX_SCALE}
              title="Zoom in"
              aria-label="Zoom in"
            >
              +
            </button>
          </div>
          )}
          {actions ? <div className="pdfviewer-actions">{actions}</div> : null}
        </div>
      </div>
      </div>
      {standard && searchOpen ? (
        <PdfSearchPanel
          standard={standard}
          pageRange={pageRange}
          currentPage={currentPage}
          numPages={numPages}
          onClose={closeSearch}
          onOpenPage={(target) => {
            if (!pages.includes(target)) return
            if (gridViewRef.current) leaveGrid(target)
            else {
              setCurrentPage(target)
              anchorToPage(target)
            }
          }}
          onRun={(rules, range) => {
            setSearchHighlightRules(rules)
            setSearchHighlightRange(range)
          }}
        />
      ) : null}
    </div>
  )
}

export default PdfViewer
